import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { IconProps } from "@insite/mobius/Icon";
import Edit from "@insite/mobius/Icons/Edit";
import Save from "@insite/mobius/Icons/Save";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps, StyledIcon } from "@insite/mobius/Link";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CopyButton from "@midland-industries/Components/CopyButton";
import updateCustomerPartNumber from "@midland-industries/Handlers/CustomerPartNumber/UpdateCustomerPartNumber";
import { CustomState } from "@midland-industries/Store/CustomerPartNumberReducer";
import loadCustomerPartByProductNumber from "@midland-industries/Store/Pages/CustomerPartNumber/Handlers/LoadCustomerPartByProductNumber";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cartLine: CartLineModel;
    isLabelBold?: boolean;
}

const mapStateToProps = (state: CustomState) => {
    const parts = state.customerParts;
    return {
        isAuthenticated: state.context.session.isAuthenticated,
        parts,
    };
};

const mapDispatchToProps = {
    updateCustomerPartNumber,
    loadCustomerPartByProductNumber,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCustomerPartNumberStyles {
    cpnWrapper?: InjectableCss;
    cpnLabel?: TypographyPresentationProps;
    cpnLabelField?: TypographyPresentationProps;
    cpnTextField?: TextFieldPresentationProps;
    cpnActionWrapper?: InjectableCss;
    cpnEditLink?: LinkPresentationProps;
    cpnEditIcon?: IconProps;
    cpnSaveLink?: LinkPresentationProps;
    cpnSaveIcon?: IconProps;
    cpnCancelLink?: LinkPresentationProps;
    cpnCancelIcon?: IconProps;
}

export const cartLineCustomerPartNumberStyles: CartLineCustomerPartNumberStyles = {
    cpnWrapper: {
        css: css`
            display: flex;
            gap: 6px;
            align-items: center;
        `,
    },
    cpnLabel: {
        css: css`
            display: inline-block;
        `,
    },
    cpnLabelField: {
        css: css`
            display: inline-block;
            margin-right: 8px;
            text-wrap: nowrap;
        `,
    },
    cpnTextField: {
        css: css`
            height: 24px !important;
            margin-left: 8px;
            &:disabled {
                color: black !important;
            }
        `,
    },
    cpnActionWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
        `,
    },
    cpnEditIcon: {
        height: 18,
    },
    cpnSaveLink: {
        css: css`
            display: inline-block;
            margin-left: 8px;
            margin-bottom: 6px;
        `,
    },
    cpnSaveIcon: {
        height: 18,
    },
    cpnCancelLink: {
        css: css`
            display: inline-block;
            margin-left: 8px;
            margin-bottom: 6px;
        `,
    },
    cpnCancelIcon: {
        height: 18,
    },
};

const CartLineCustomerPartNumber: React.FC<Props> = ({
    cartLine,
    isLabelBold = false,
    isAuthenticated,
    parts,
    updateCustomerPartNumber,
    loadCustomerPartByProductNumber,
}) => {
    const [newCustomerPartNumber, setNewCustomerPartNumber] = React.useState("");
    const [editCustomerPartNumber, setEditCustomerPartNumber] = React.useState(false);

    if (!isAuthenticated) {
        return null;
    }

    useEffect(() => {
        if (newCustomerPartNumber.length === 0) {
            if (cartLine && cartLine.productId) {
                const customerPart = parts?.byId[cartLine.productId];
                if (customerPart) {
                    setNewCustomerPartNumber(customerPart.customerPartNumber);
                }
            }
        }
    }, [parts]);

    useEffect(() => {
        (() => {
            if (cartLine && cartLine.productId) {
                loadCustomerPartByProductNumber({
                    productId: cartLine.productId,
                    erpNumber: cartLine.erpNumber,
                });
            }
        })();
    }, []);

    const styles = cartLineCustomerPartNumberStyles;

    const handleSaveClick = (
        event: React.MouseEvent<Element, MouseEvent> | React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    ) => {
        event.preventDefault();
        updateCustomerPartNumber({
            customerPart: {
                customerPartNumber: newCustomerPartNumber,
                id: cartLine.id,
                erpNumber: cartLine.erpNumber,
            },
            onComplete: () => {
                setEditCustomerPartNumber(false);
            },
        });
    };

    return (
        <StyledWrapper {...styles.cpnWrapper}>
            {editCustomerPartNumber ? (
                <>
                    <TextField
                        {...styles.cpnTextField}
                        disabled={!editCustomerPartNumber}
                        onChange={e => setNewCustomerPartNumber(e.target.value)}
                        value={newCustomerPartNumber}
                    />
                    <StyledWrapper {...styles.cpnActionWrapper}>
                        <Link {...styles.cpnSaveLink} onClick={e => handleSaveClick(e)}>
                            <StyledIcon src={Save} {...styles.cpnSaveIcon} />
                        </Link>
                        <Link {...styles.cpnCancelLink} onClick={() => setEditCustomerPartNumber(false)}>
                            <StyledIcon src={X} {...styles.cpnCancelIcon} />
                        </Link>
                    </StyledWrapper>
                </>
            ) : (
                <>
                    <Typography size={16} color="#2E64B0" weight={isLabelBold ? 700 : 400}>
                        {translate("My Part #")}
                    </Typography>
                    <Typography size={16} color="#2E64B0" weight={isLabelBold ? 700 : 400}>
                        {newCustomerPartNumber}
                    </Typography>
                    <StyledIcon
                        style={{ cursor: "pointer" }}
                        src={Edit}
                        size={16}
                        color="#2E64B0"
                        onClick={() => setEditCustomerPartNumber(true)}
                    />
                    <CopyButton value={newCustomerPartNumber} style={{ cursor: "pointer", marginLeft: -6 }} />
                </>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CartLineCustomerPartNumber);
